import { PlayButtonIcon } from 'assets/icons';
import VideoFrame from 'components/molecules/VideoFrame';
import { useConfig } from 'context/app-config';
import { AnimatePresence, motion } from 'framer-motion';
import type { Dispatch, SetStateAction } from 'react';
import React, { useCallback } from 'react';
import { styled } from 'styled-components';

import BackButton from '../BackButton';
import SpinnerLoader from '../Loaders/spinnerLoader';
import styles from './styles.module.scss';

const StyledComponent = styled(motion.div)`
  z-index: 10000;
`;

interface FullScreenPlayerProps {
  videoSrc: string;
  thumbnail: string;
  setVideoPlaying?: Dispatch<SetStateAction<boolean>>;
  thumbnailWidth?: string;
  thumbnailHeight?: string;
  hidePlayButton?: boolean;
}

const containerVariant = {
  collapsed: { y: '100%' },
  open: { y: '0%' },
};

const ANIMATION_DURATION = 0.2;

const FullScreenPlayer: React.FC<FullScreenPlayerProps> = ({
  videoSrc,
  thumbnail,
  setVideoPlaying,
  thumbnailWidth,
  thumbnailHeight,
  hidePlayButton = false,
}) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isPaused, setIsPaused] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const config = useConfig();
  const [showFullScreenVideo, setShowFullScreenVideo] = React.useState(false);
  const [showPlayIcon, setShowPlayIcon] = React.useState(true);

  React.useEffect(() => {
    if (config?.deniesInlineMediaPlayback) {
      return;
    }
    if (videoRef && videoRef.current) {
      if (isPaused) {
        videoRef.current?.pause();
      } else {
        videoRef.current?.play();
      }
    }
  }, [config?.deniesInlineMediaPlayback, isPaused]);

  const handleEndVideo = useCallback(() => {
    // Pause the video
    videoRef.current?.pause();
    // show poster image & reset video
    videoRef.current?.load();
    setShowPlayIcon(true);
  }, []);

  React.useEffect(() => {
    if (!config?.deniesInlineMediaPlayback) {
      return undefined;
    }

    const videoEl = videoRef.current;
    videoRef.current?.addEventListener('webkitendfullscreen', handleEndVideo);

    return () => {
      videoEl?.removeEventListener('webkitendfullscreen', handleEndVideo);
    };
  }, [config?.deniesInlineMediaPlayback, handleEndVideo]);

  const togglePause = React.useCallback((value: boolean) => setIsPaused(value), []);

  const handleContextMenu = (e: React.MouseEvent<HTMLVideoElement, MouseEvent>) => e.preventDefault(); // don't open popup on mobile when long-pressed

  const handleClick = (element: EventTarget) => {
    if ((element as HTMLElement).id === 'playerContainer') {
      setShowFullScreenVideo(false);
    }
  };

  const handleLoadedData = () => {
    if (videoRef && videoRef.current) {
      setIsLoaded(true);
      videoRef.current.play();
    }
  };

  // for webviews that have allowsInlineMediaPlayback set to false (iOS)
  // https://developer.apple.com/documentation/webkit/wkwebviewconfiguration/1614793-allowsinlinemediaplayback
  if (config?.deniesInlineMediaPlayback) {
    return (
      <div className={styles.posterContainer}>
        {showPlayIcon && !hidePlayButton && (
          <PlayButtonIcon
            onClick={() => {
              setShowPlayIcon(false);
              videoRef.current?.play();
              setVideoPlaying?.(true);
            }}
            className={styles.button}
          />
        )}
        <video
          // eslint-disable-next-line react/no-unknown-property
          onLoad={() => setShowPlayIcon(true)}
          onPause={() => setShowPlayIcon(true)}
          onPlay={() => setShowPlayIcon(false)}
          className={styles.image}
          ref={videoRef}
          poster={thumbnail}
          src={videoSrc}
          onEnded={handleEndVideo}
          onClick={() => {
            if (videoRef.current?.paused === true) {
              // Play the video
              setShowPlayIcon(false);
              setVideoPlaying?.(true);
              videoRef.current?.play();
            } else {
              // Pause the video
              videoRef.current?.pause();
              // show poster image & reset video
              videoRef.current?.load();
              setShowPlayIcon(true);
              setShowPlayIcon(false);
            }
          }}
        >
          <track kind="captions" />
        </video>
      </div>
    );
  }

  return (
    <>
      <VideoFrame
        hidePlayButton={hidePlayButton}
        width={thumbnailWidth}
        height={thumbnailHeight}
        thumbnail={thumbnail}
        playVideo={() => {
          setShowFullScreenVideo(true);
          setVideoPlaying?.(true);
        }}
      />
      <AnimatePresence>
        {showFullScreenVideo && (
          <StyledComponent
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={containerVariant}
            transition={{
              duration: ANIMATION_DURATION,
            }}
            id="playerContainer"
            className={styles.playerContainer}
            onClick={(e) => handleClick(e.target)}
          >
            <div className={styles.player}>
              {!isLoaded && (
                <div className={styles.loading}>
                  <SpinnerLoader />
                </div>
              )}
              <div className={styles.header}>
                <BackButton
                  onClick={() => {
                    setShowFullScreenVideo(false);
                    setVideoPlaying?.(false);
                  }}
                  className="d-flex p-0"
                />
                {isPaused && <p className={styles.paused}>Paused</p>}
              </div>
              <video
                ref={videoRef}
                src={videoSrc}
                playsInline
                controls={false}
                autoPlay
                onMouseDown={() => togglePause(true)}
                onTouchStart={() => togglePause(true)}
                onMouseUp={() => togglePause(false)}
                onTouchEnd={() => togglePause(false)}
                onContextMenu={handleContextMenu}
                onEnded={() => setShowFullScreenVideo(false)}
                onError={() => setShowFullScreenVideo(false)}
                onLoadedData={handleLoadedData}
              >
                <track kind="captions" />
              </video>
            </div>
          </StyledComponent>
        )}
      </AnimatePresence>
    </>
  );
};

export default FullScreenPlayer;
